import StyledTooltip from "@/components/styled-tooltip";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  CircleCheck,
  CircleX,
  Inbox,
  Mail,
  MailWarning,
  OctagonAlert,
  AlertCircle,
} from "lucide-react";
import { useMemo, useRef, useState, useEffect } from "react";

import {
  Envelope,
  Icon,
  Info as InfoIcon,
  Tag as TagIcon,
  Tray,
  Users as UsersIcon,
} from "@phosphor-icons/react";
import { mapToColorClasses } from "@/lib/colors";
import { EmailDetails } from "./email-details";
import { getTooltip } from "../email-health/email-health-tooltips";
import { useEmailDetails } from "@/hooks/use-inboxplacement";
import { EmailDetailResponse, EmailProvider } from "@/types/inboxPlacements.types";

const getStatusIcon = (status: string) => {
  switch (status) {
    case "Inbox":
      return <Inbox className="text-green-600" />;
    case "Spam":
      return <OctagonAlert className="text-red-600" />;
    case "Unreceived":
      return <MailWarning className="text-yellow-600" />;
    default:
      return null;
  }
};

const getPercentageColor = (status: string, value: number) => {
  switch (status) {
    case "Inbox":
      return "text-green-600";
    case "Spam":
      return "text-red-600";
    case "Unreceived":
      return "text-yellow-600";
    default:
      return "text-muted-foreground";
  }
};

export interface IProviderData {
  provider: string;
  providerName: EmailProvider;
  testId: string | null;
  stats: {
    inbox: number;
    spam: number;
    unreceived: number;
  };
  category: string[];
  emails: {
    address: string;
    status: string;
    category?: string;
    senderIp?: string;
    authStatus: { dkim: boolean; spf: boolean; dmarc: boolean };
  }[];
  forceOpen?: boolean;
  isLoading?: boolean;
}
export interface IProviderDataState {
  b2c: IProviderData[];
  b2b: IProviderData[];
}

const iconMap: Record<string, Icon> = {
  primary: Inbox,
  promotion: TagIcon,
  social: UsersIcon,
  updates: InfoIcon,
  focused: Inbox,
  others: Envelope,
  newsletter: TagIcon,
  notification: InfoIcon,
  vip: Envelope,
  inbox: Inbox,
};

const ProviderCollapes = ({
  provider,
  stats,
  emails = [],
  testId,
  category = [],
  providerName,
  forceOpen = false,
  isLoading = false,
}: IProviderData) => {
  const [isOpen, setIsOpen] = useState(true);
  const statuses = [
    { key: "inbox", label: "Inbox", value: stats?.inbox || 0 },
    { key: "spam", label: "Spam", value: stats?.spam || 0 },
    { key: "unreceived", label: "Unreceived", value: stats?.unreceived || 0 },
  ];

  const categoryBreakdown = useMemo(() => {
    if (!category || category.length === 0) return [];

    const inboxEmails = emails.filter((email) => email.status === "Inbox");
    const totalInboxEmails = inboxEmails.length;
    const totalInboxPercentage = stats.inbox;

    // If no inbox emails, return empty breakdown
    if (totalInboxEmails === 0) return [];

    const breakdown = category.map((cat) => {
      const categoryEmails = inboxEmails.filter(
        (email) => email.category?.toLowerCase() === cat.toLowerCase()
      ).length;

      // Calculate this category's share of the total inbox percentage
      const categoryPercentage =
        (categoryEmails / totalInboxEmails) * totalInboxPercentage;

      return {
        label: cat.toLowerCase(),
        value: categoryPercentage,
        progressBarValue: (categoryEmails / totalInboxEmails) * 100,
      };
    });

    return breakdown;
  }, [emails, category, stats.inbox]);

  if (isLoading) {
    return (
      <div className="border rounded-lg overflow-hidden bg-background">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Skeleton className="h-8 w-40" />
            </div>
          </div>
          <div className="mt-4">
            <Skeleton className="h-3 w-full" />
          </div>
          <div className="flex items-center gap-4 py-2 flex-wrap mt-4">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </div>
    );
  }

  if (!emails || emails.length === 0) {
    return (
      <div className="border rounded-lg overflow-hidden bg-background">
        <div className="p-6 flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
          <h3 className="font-medium text-lg mb-1">{provider}</h3>
          <p className="text-muted-foreground mb-4">
            No data available for this provider
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group border rounded-lg transition-colors overflow-hidden bg-background"
      )}
    >
        <button
        className="w-full flex items-center p-3 sm:p-4 text-left cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm sm:text-base font-medium">{provider}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Emails: {emails.length}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center rounded-md overflow-hidden">
                {statuses.map((status) => (
                  <StyledTooltip
                    key={status.key}
                    description={`${status.label} - ${status.value}%`}
                  >
                    <div
                      className={cn(
                        "flex items-center min-w-[60px] px-2 gap-1",
                        mapToColorClasses(status.key, ["bg", "text"])
                      )}
                    >
                      <span className="[&_svg]:size-4">
                        {getStatusIcon(status.label)}
                      </span>
                      <span className="text-sm font-medium">
                        {status.value}%
                      </span>
                    </div>
                  </StyledTooltip>
                ))}
              </div>
            </div>
          </div>
        </div>
        <ChevronRight
          className={`h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground transition-transform ml-3 sm:ml-6 ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </button>

      {/* ESP-specific Category Legend with common statuses */}
      <div className="px-3 sm:px-4 pb-2 sm:pb-3">
        <div className="md:hidden flex h-2 rounded-full overflow-hidden mt-2">
          {statuses.map((status) => (
            <div
              key={status.key}
              className={mapToColorClasses(status.key, ["bg_500"])}
              style={{ width: `${status.value}%` }}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-4 py-2 flex-wrap">
          {/* Show ESP-specific categories if available */}
          {category && category.length > 0 ? (
            category.map((cat) => {
              const key = cat.toLowerCase();
              const Icon = iconMap[key] || Mail;

              return (
                <div key={key} className="flex items-center gap-1 sm:gap-2">
                  <Icon
                    weight="duotone"
                    className={cn(
                      "size-3.5 sm:size-4",
                      mapToColorClasses(`category_${key}`, ["text"])
                    )}
                  />

                  <span className="text-xs sm:text-sm capitalize font-medium">
                    {key}
                  </span>
                  <div
                    className={cn(
                      "w-2 h-2 sm:w-3 sm:h-3 rounded-full",
                      mapToColorClasses(`category_${key}`, ["bg_500"])
                    )}
                  />
                </div>
              );
            })
          ) : (
            /* Show default Inbox status if no categories */
            <div className="flex items-center gap-1 sm:gap-2">
              <Inbox className="size-3.5 sm:size-4 text-green-600" />

              <span className="text-xs sm:text-sm capitalize font-medium">
                Inbox
              </span>
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500" />
            </div>
          )}

          {/* Common statuses - always show Spam and Unreceived */}
          <div className="flex items-center gap-1 sm:gap-2">
            <OctagonAlert className="size-3.5 sm:size-4 text-red-600" />

            <span className="text-xs sm:text-sm capitalize font-medium">
              Spam
            </span>
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500" />
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <MailWarning className="size-3.5 sm:size-4 text-yellow-600" />

            <span className="text-xs sm:text-sm capitalize font-medium">
              Unreceived
            </span>
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-500" />
          </div>
        </div>
      </div>

      {(isOpen || forceOpen) && (
        <ProviderCollapesContent
          categoryBreakdown={categoryBreakdown}
          statuses={statuses}
          emails={emails}
          testId={testId}
          provider={provider}
          providerName={providerName}
        />
      )}
    </div>
  );
};

const ProviderCollapesContent = ({
  categoryBreakdown,
  emails,
  statuses,
  testId,
  provider,
  providerName,
}: {
  categoryBreakdown: {
    label: string;
    value: number;
    progressBarValue: number;
  }[];
  testId: string | null;
  provider: string;
  statuses: {
    key: string;
    label: string;
    value: number;
  }[];
  providerName: EmailProvider;
  emails: {
    address: string;
    status: string;
    category?: string;
    senderIp?: string;
    authStatus: { dkim: boolean; spf: boolean; dmarc: boolean };
  }[];
}) => {
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const { data: emailDetails, isLoading: isEmailDetailsLoading } =
    useEmailDetails(testId || "", providerName, selectedEmail || "");

  // Only log when emailDetails exists
  // console.log(emailDetails, "emailDetails");

  const handleEmailClick = (address: string) => {
    setSelectedEmail(address);
  };
  return (
    <div className="border-t bg-muted/50">
      <div className="space-y-3">
        {/* Category Breakdown */}
        {categoryBreakdown.length > 0 && (
          <div className="px-3 sm:px-4 py-2">
            <span className="text-xs sm:text-sm text-muted-foreground">
              Inbox Category Breakdown
            </span>
            <div className="flex h-2 sm:h-3 rounded-full overflow-hidden mt-1 sm:mt-2">
              {categoryBreakdown.map((cat) => (
                <div
                  key={cat.label}
                  className={cn(
                    mapToColorClasses(`category_${cat.label}`, ["bg_500"])
                  )}
                  style={{ width: `${cat.progressBarValue}%` }}
                />
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-4 py-2 flex-wrap">
              {categoryBreakdown.map((cat) => {
                const Icon = iconMap[cat.label] || Mail;

                if (cat.label === "social") return null;
                return (
                  <div
                    key={cat.label}
                    className="flex items-center gap-1 sm:gap-2"
                  >
                    <Icon
                      weight="duotone"
                      className={cn(
                        "size-3.5 sm:size-4",
                        mapToColorClasses(`category_${cat.label}`, ["text"])
                      )}
                    />
                    <span className="text-xs sm:text-sm text-muted-foreground capitalize font-medium">
                      {cat.label}
                    </span>
                    <Badge
                      variant="outline"
                      className="bg-slate-100 text-slate-500 dark:bg-white/20 dark:text-white px-1.5 sm:px-2 border-none text-xs"
                    >
                      {cat.value.toFixed(1)}%
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Status Progress Bar (for ESPs without categories) */}
        {(!categoryBreakdown || categoryBreakdown.length === 0) && (
          <div className="px-3 sm:px-4 pt-2 sm:pt-3 pb-1 sm:pb-2">
            {/* Don't show this on mobile - we already have a progress bar */}
            <div className="hidden md:flex h-2 sm:h-3 rounded-full overflow-hidden">
              {statuses.map((status) => (
                <div
                  key={status.key}
                  className={mapToColorClasses(status.key, ["bg_500"])}
                  style={{ width: `${status.value}%` }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 sm:gap-4 py-2 flex-wrap">
              {statuses.map((status) => (
                <div
                  key={status.key}
                  className="flex items-center gap-1 sm:gap-2"
                >
                  <span className="[&_svg]:size-3.5 sm:[&_svg]:size-4">
                    {getStatusIcon(status.label)}
                  </span>
                  <span className="text-xs sm:text-sm font-medium">
                    {status.label}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-slate-100 text-slate-500 dark:bg-white/20 dark:text-white px-1.5 sm:px-2 border-none text-xs"
                  >
                    {status.value}%
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-sm">
          {emails.length === 0 ? (
            <div className="p-10 flex flex-col items-center justify-center text-center">
              <MailWarning className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No email data available</p>
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10 hidden sm:table-header-group">
                <TableRow>
                  <TableHead className="whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      Email
                      <StyledTooltip description="Click on an email to view detailed information">
                        <InfoIcon
                          weight="fill"
                          className="size-3 sm:size-3.5 text-blue-500 dark:text-blue-400"
                        />
                      </StyledTooltip>
                    </div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">
                    Delivered to
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Sender IP</TableHead>
                  <TableHead className="whitespace-nowrap">
                    <StyledTooltip description={getTooltip("DKIM")}>
                      <span className="underline">DKIM</span>
                    </StyledTooltip>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">
                    <StyledTooltip description={getTooltip("SPF")}>
                      <span className="underline">SPF</span>
                    </StyledTooltip>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">
                    <StyledTooltip description={getTooltip("DMARC")}>
                      <span className="underline">DMARC</span>
                    </StyledTooltip>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">
                    <StyledTooltip description="View authentication & delivery details">
                      <span className="underline">Action</span>
                    </StyledTooltip>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="block sm:table-row-group w-full">
                {emails.map((email, index) => (
                  <TableRow
                    key={`${email.address}-${index}`}
                    onClick={() => handleEmailClick(email.address)}
                    className="odd:bg-muted/50 block sm:table-row w-full"
                  >
                    <TableCell className="block sm:table-cell py-2 sm:py-3 text-xs sm:text-sm w-full sm:w-auto">
                      {/* Desktop view */}
                      <div className="hidden sm:block">
                        {email.status === "Unreceived" ? (
                          <span>{email.address}</span>
                        ) : (
                          <EmailDetails
                            data={emailDetails as EmailDetailResponse}
                            email={email.address}
                            isLoading={isEmailDetailsLoading}
                            providerKey={providerName}
                          />
                        )}
                      </div>

                      {/* Mobile view */}
                      <div className="sm:hidden space-y-1.5 w-full">
                        <div className="flex gap-2">
                          <span className=" text-muted-foreground min-w-[80px]">
                            Email:
                          </span>
                          <span className="text-wrap break-all">
                            {email.status.toLowerCase() === "unreceived" ? (
                              <span>{email.address}</span>
                            ) : (
                              <EmailDetails
                                data={emailDetails as EmailDetailResponse}
                                email={email.address}
                                isLoading={isEmailDetailsLoading}
                                providerKey={providerName}
                              />
                            )}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <span className=" text-muted-foreground min-w-[80px]">
                            Delivered to:
                          </span>
                          <div className="flex items-center text-xs font-medium uppercase">
                            <span
                              className={cn(
                                "px-2 py-0.5 sm:py-1 rounded-l-md",
                                !email.category && "rounded-r-md",
                                mapToColorClasses(email.status.toLowerCase(), [
                                  "bg",
                                  "text",
                                ])
                              )}
                            >
                              {email.status}
                            </span>
                            {(email.category || email.status !== "social") && (
                              <span
                                className={cn(
                                  "px-2 py-0.5 sm:py-1 rounded-r-md",
                                  mapToColorClasses(
                                    `category_${email.category}`,
                                    ["bg", "text"]
                                  )
                                )}
                              >
                                {email.category}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <span className=" text-muted-foreground min-w-[80px]">
                            Sender IP:
                          </span>
                          <span className="text-wrap break-all">
                            {email.senderIp}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <span className=" text-muted-foreground min-w-[80px]">
                            Auth Status:
                          </span>
                          <div className="flex gap-1">
                            <Badge
                              variant="outline"
                              className={cn(
                                "border-none",
                                email?.authStatus.dkim
                                  ? "bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-600"
                                  : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-600"
                              )}
                            >
                              DKIM
                            </Badge>

                            <Badge
                              variant="outline"
                              className={cn(
                                "border-none",
                                email?.authStatus.spf
                                  ? "bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-600"
                                  : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-600"
                              )}
                            >
                              SPF
                            </Badge>

                            <Badge
                              variant="outline"
                              className={cn(
                                "border-none",
                                email?.authStatus.dmarc
                                  ? "bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-600"
                                  : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-600"
                              )}
                            >
                              DMARC
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 sm:py-3 hidden sm:table-cell">
                      <div className="flex items-center text-xs font-medium uppercase">
                        <span
                          className={cn(
                            "px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-l-md",
                            !email.category && "rounded-r-md",
                            mapToColorClasses(email.status.toLowerCase(), [
                              "bg",
                              "text",
                            ])
                          )}
                        >
                          {email.status}
                        </span>
                        {(email.category || email.status !== "social") && (
                          <span
                            className={cn(
                              "px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-r-md",
                              mapToColorClasses(`category_${email.category}`, [
                                "bg",
                                "text",
                              ])
                            )}
                          >
                            {email.category}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 sm:py-3 text-xs sm:text-sm hidden sm:table-cell">
                      {email.status.toLowerCase() === "unreceived" ? (
                        <span>{email.senderIp}</span>
                      ) : (
                        <span>{email.senderIp}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 sm:py-3 hidden sm:table-cell">
                      {email.authStatus.dkim === false ? (
                        <CircleX className="text-red-500 size-4 sm:size-5" />
                      ) : (
                        <CircleCheck className="text-green-500 size-4 sm:size-5" />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 sm:py-3 hidden sm:table-cell">
                      {email.authStatus.spf === false ? (
                        <CircleX className="text-red-500 size-4 sm:size-5" />
                      ) : (
                        <CircleCheck className="text-green-500 size-4 sm:size-5" />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 sm:py-3 hidden sm:table-cell">
                      {email.authStatus.dmarc === false ? (
                        <CircleX className="text-red-500 size-4 sm:size-5" />
                      ) : (
                        <CircleCheck className="text-green-500 size-4 sm:size-5" />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2 sm:py-3 hidden sm:table-cell">
                    <EmailDetails
                            data={emailDetails as EmailDetailResponse}
                            email={email.address}
                            isLoading={isEmailDetailsLoading}
                            providerKey={providerName}
                            showAction={true}
                          />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

// Add a skeleton component for provider collapses
export const ProviderCollapesSkeleton = () => {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="border rounded-lg overflow-hidden bg-background"
        >
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="hidden md:flex items-center gap-2">
                <Skeleton className="h-8 w-40" />
              </div>
            </div>
            <div className="mt-4">
              <Skeleton className="h-3 w-full" />
            </div>
            <div className="flex items-center gap-4 py-2 flex-wrap mt-4">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProviderCollapes;
